import React from 'react';
import PropTypes from 'prop-types';
import './qtyStepper.css';

/* QtyStepper — SP-main's `qtyHTML()` (js/storefront-shell.js l.975-982).

   The minus button at quantity 1 is the caller's decision: on the cart surfaces
   stepping down from 1 removes the line, on the product page it stops at 1.
   Encoding either here would make the other surface wrong, so the caller passes
   `min` (clamp) or `onDecrementAtMin` (remove).

   Every control is labelled, because "−", "3" and "+" read as nothing in a
   screen reader's forms list. `itemLabel` folds the product's name into those
   labels so several steppers on one page are told apart. */
export default function QtyStepper({
  value,
  onChange,
  onDecrementAtMin,
  min,
  max,
  small,
  disabled,
  itemLabel,
  className,
}) {
  const qty = Number(value) || min;
  const suffix = itemLabel ? ` of ${itemLabel}` : '';

  const atMin = qty <= min;
  /* Only a tracked, known ceiling disables the control: `max` of null means the
     brand does not track inventory, which is not "none left". */
  const atMax = max != null && qty >= max;

  const step = (delta) => {
    const next = qty + delta;
    if (delta < 0 && atMin) {
      if (onDecrementAtMin) onDecrementAtMin();
      return;
    }
    if (next < min) return;
    if (max != null && next > max) return;
    onChange(next);
  };

  /* Typed input is clamped on the way out, never on the way in — rewriting the
     field mid-keystroke fights the person typing "12" through "1". */
  const onType = (e) => {
    const raw = parseInt(e.target.value, 10);
    if (Number.isNaN(raw)) return;
    let next = Math.max(min, raw);
    if (max != null) next = Math.min(max, next);
    onChange(next);
  };

  const classes = ['sf-qty', small ? 'is-sm' : '', className].filter(Boolean).join(' ');

  return (
    <span className={classes}>
      <button
        type="button"
        onClick={() => step(-1)}
        disabled={disabled || (atMin && !onDecrementAtMin)}
        aria-label={`Reduce quantity${suffix}`}
      >
        &minus;
      </button>
      <input
        type="text"
        inputMode="numeric"
        value={qty}
        onChange={onType}
        disabled={disabled}
        aria-label={`Quantity${suffix}`}
      />
      <button
        type="button"
        onClick={() => step(1)}
        disabled={disabled || atMax}
        aria-label={`Increase quantity${suffix}`}
      >
        +
      </button>
    </span>
  );
}

QtyStepper.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onChange: PropTypes.func.isRequired,
  /** Called instead of onChange when stepping below `min` — the cart removes the line. */
  onDecrementAtMin: PropTypes.func,
  min: PropTypes.number,
  /** Inventory ceiling, or null where the brand does not track stock. */
  max: PropTypes.number,
  /** `.is-sm` — what both cart surfaces use. */
  small: PropTypes.bool,
  disabled: PropTypes.bool,
  /** Product name, folded into the control labels so rows are told apart. */
  itemLabel: PropTypes.string,
  className: PropTypes.string,
};

QtyStepper.defaultProps = {
  onDecrementAtMin: null,
  min: 1,
  max: null,
  small: false,
  disabled: false,
  itemLabel: '',
  className: '',
};
